import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import { CellParams, ColDef, DataGrid } from "@material-ui/data-grid";
import UpdatableCountField, {
  Button,
} from "components/Admin/Tables/columns/UpdatableCountField";
import { selectLoading } from "features/admin/Products/productsAdminSlice";
import { ObjectType } from "features/types";
import React, { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/rootReducer";
import styled from "styled-components/macro";
import { fetchOrderedElements, setOrderElements } from "../ordersAdminSlice";
import OrderProducts from "./OrderProducts";

const ObjectTypeToInstallation: { [key: string]: boolean } = {
  [ObjectType.product]: true,
  [ObjectType.accessory]: false,
};

interface AddOrderProductTableProps {
  setValue: any;
  getValues: any;
  selector: (state: RootState) => any;
  fetchFN: any;
  objectType: any;
  activeValues?: any;
}

const AddOrderProductTable: React.FC<AddOrderProductTableProps> = ({
  setValue,
  getValues,
  selector,
  fetchFN,
  objectType,
  activeValues,
}) => {
  const dispatch = useDispatch();

  const isLoading = useSelector(selectLoading);
  const [orderedItems, setOrderedItems] = useState<any>([]);
  const products: any = useSelector(selector)?.map((element: any) => {
    return {
      ...element,
      installation: false,
      objectType: ObjectType.product,
    };
  });
  const [filteredRows, setFilteredRows] = useState<any[]>(products);

  useEffect(() => {
    if (filteredRows.length === 0 && products.length > 0) {
      setFilteredRows(
        products.map((item: any) => {
          return {
            ...item,
            ordered: 0,
            installation: false,
          };
        })
      );
    }
  }, [products]);

  useEffect(() => {
    if (products.length === 0) {
      dispatch(fetchFN());
    }

    if (activeValues) {
      const items = activeValues.items;
      dispatch(
        setOrderElements(
          items.map((item: any) => {
            return { product: item, count: item.count };
          })
        )
      );

      setValue("items", [
        ...items.map((item: any) => {
          return {
            id: item.refId,
            count: item.count,
            objectType: item.objectType,
          };
        }),
      ]);
    }
  }, []);

  useEffect(() => {
    if (orderedItems.length !== 0) {
      dispatch(fetchOrderedElements(orderedItems));
    }
  }, [orderedItems]);

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchWord = event.target.value;
    const newRows = products
      .filter((item: any) =>
        item.title.toLowerCase().includes(searchWord.toLowerCase())
      )
      .map((item: any) => {
        return {
          ...item,
          ordered: 0,
          installation: false,
          objectType,
        };
      });
    setFilteredRows(newRows);
  };

  const addProductToOrder = (ev: any, params: any) => {
    ev.preventDefault();
    updateItems(params);
  };

  const updateItems = (params: any) => {
    const currentItems = getValues("items");
    const itemToAdd = currentItems.find(
      (item: any) => item.id === params.row.refId
    );
    if (itemToAdd) {
      itemToAdd.count += params.row.ordered;
      itemToAdd.installation += params.row.installation;
    } else {
      const orderItem = {
        id: params.row.refId,
        count: params.row.ordered,
        installation: params.row.installation,
        objectType,
      };
      currentItems.push(orderItem);
    }

    const newFilteredRows = filteredRows.map((item) => {
      return item.refId === params.row.refId
        ? { ...item, ordered: 0, installation: false }
        : { ...item };
    });
    setFilteredRows(newFilteredRows);
    setOrderedItems([...currentItems]);
  };

  const getUpdateItem = (params: any) => {
    const newFilteredRows = [...filteredRows];
    const itemToUpdate = newFilteredRows.find(
      (item) => item.id === params.row.id
    );

    const updateRows = () => {
      setFilteredRows(newFilteredRows);
    };

    return { itemToUpdate, updateRows };
  };

  const minusHandler = (params: CellParams) => {
    const { itemToUpdate, updateRows } = getUpdateItem(params);
    if (itemToUpdate) {
      itemToUpdate.ordered = itemToUpdate.ordered - 1;
      itemToUpdate.quantity = itemToUpdate.quantity + 1;
      updateRows();
    }
  };

  const plusHandler = (params: CellParams) => {
    const { itemToUpdate, updateRows } = getUpdateItem(params);
    if (itemToUpdate) {
      itemToUpdate.ordered = itemToUpdate.ordered + 1;
      itemToUpdate.quantity = itemToUpdate.quantity - 1;
      updateRows();
    }
  };

  const onInstallationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    params: CellParams
  ) => {
    const installation = event.target.checked;
    const currentItems = getValues("items");
    const itemToAdd = currentItems.find(
      (item: any) => item.id === params.row.refId
    );
    if (itemToAdd) {
      itemToAdd.installation += installation;
    }

    setFilteredRows(
      filteredRows.map((item) => {
        return item.refId === params.row.refId
          ? { ...item, installation }
          : { ...item };
      })
    );
  };
  let columns: ColDef[] = [
    { sortable: false, field: "title", headerName: "Име", width: 700 },
    { sortable: false, field: "quantity", headerName: "Наличност", width: 150 },
    {
      field: "ordered",
      headerName: "Брой",
      width: 200,
      renderCell: (params) => {
        return (
          <UpdatableCountField
            onMinusClickHandler={minusHandler}
            onPlusClickHandler={plusHandler}
            rowParams={params}
            isMinusDisabled={params.row.ordered === 0}
            isPlusDisabled={params.row.quantity <= 0}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "Действие",
      width: 200,
      renderCell: (params) => {
        return (
          <Button
            onClick={(event) => addProductToOrder(event, params)}
            disabled={params.row.ordered === 0}
          >
            Добави
          </Button>
        );
      },
    },
  ];

  if (ObjectTypeToInstallation[objectType]) {
    columns = [
      ...columns,
      {
        field: "installation",
        headerName: "С монтаж",
        width: 200,
        renderCell: (params: CellParams) => {
          return (
            <Checkbox
              color="primary"
              onChange={(e) => onInstallationChange(e, params)}
              checked={params.row.installation}
              name="installation"
            />
          );
        },
      },
    ];
  }

  const removeItemHandler = (id: string) => {
    const currentItems: any[] = getValues("items");
    const itemIdx = currentItems.findIndex((item: any) => item.id === id);
    const itemToDelete = currentItems[itemIdx];

    if (itemIdx !== -1) {
      currentItems.splice(itemIdx, 1);
    }

    setFilteredRows(
      filteredRows.map((item) => {
        return item.refId === itemToDelete.id
          ? { ...item, quantity: item.quantity + itemToDelete.count }
          : { ...item };
      })
    );

    if (currentItems.length === 0) {
      setValue("items", []);
      dispatch(fetchOrderedElements([]));
    }

    setOrderedItems([...currentItems]);
  };

  return (
    <AddOrderProductTableContainer>
      <SearchField
        size="small"
        variant="outlined"
        label="Търси"
        id="outlined-basic"
        onChange={onSearch}
      />
      <TableContainer>
        <DataGrid
          columns={columns}
          autoPageSize={true}
          rows={filteredRows}
          disableSelectionOnClick
          disableColumnFilter
          loading={isLoading}
        />
      </TableContainer>
      <OrderProducts
        noProductsMessage="Няма продукти добавени към поръчката"
        removeItemHandler={removeItemHandler}
        objectTypes={[objectType]}
      />
    </AddOrderProductTableContainer>
  );
};

const AddOrderProductTableContainer = styled.div`
  width: 100%;
  display: flex;
  flex-flow: column;
`;

const TableContainer = styled.div`
  width: 100%;
  height: 400px;
`;

const SearchField = styled(TextField)`
  width: 320px;
  margin: 10px 0;
`;

export default memo(AddOrderProductTable);
